import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useConfigure } from "react-instantsearch";
import { Loader } from "@googlemaps/js-api-loader";
import qlog from "views_shared/app/javascript/scripts/qlog";

function GeoSearchByPlace({
  initialLocationName = undefined,
  initialLocationRadius = undefined,
  onActiveChange = undefined,
  onLabelChange = undefined,
  resetSignal = undefined,
}) {
  const [placeValue, setPlaceValue] = useState();
  const [latLng, setLatLng] = useState({});
  const [aroundRadius, setAroundRadius] = useState("all");
  const placeInputRef = useRef();

  // Guards for the delayed initialLocationName flow: initTimeoutRef lets a clear cancel the timer;
  // initGenerationRef invalidates an in-flight Places callback so a late setLatLng can't silently
  // reactivate geo filtering. The generation governs only the initial-location flow, never whether
  // Autocomplete installs.
  const initTimeoutRef = useRef(null);
  const initGenerationRef = useRef(0);

  const submitIconPath =
    "M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5C5.62 9.5 4.5 8.38 4.5 7S5.62 4.5 7 4.5 9.5 5.62 9.5 7 8.38 9.5 7 9.5z";
  const resetIconPath =
    "M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z";

  const hasLocation = Object.keys(latLng).length > 0;

  // Explicit `undefined` (rather than an unmounted widget, or reconfiguring with empty strings) is
  // what actually removes aroundLatLng/aroundRadius from the outgoing Algolia request: the app's
  // InstantSearch instance sets `future.preserveSharedStateOnUnmount`, so a widget's last-configured
  // parameters survive it unmounting — this stays mounted and passes `undefined` to unset the keys
  // instead. A stale real aroundLatLng left over from a previously chosen (then cleared) location is
  // the actual danger here: geo mode filters out any record with no _geoloc attribute, so once real
  // coordinates leak into every subsequent query, plain-text search silently drops such records.
  useConfigure(
    hasLocation
      ? { aroundLatLng: `${latLng.lat}, ${latLng.lng}`, aroundRadius }
      : { aroundLatLng: undefined, aroundRadius: undefined },
  );

  useEffect(() => {
    onActiveChange?.(hasLocation);
  }, [hasLocation, onActiveChange]);

  useEffect(() => {
    onLabelChange?.(placeValue ?? null);
  }, [placeValue, onLabelChange]);

  // Allow the place to be programatically set (used by initialLocationName). If a clear/reset/unmount
  // bumps `generation` before findPlaceFromQuery resolves, the callback is stale and discarded.
  const setPlace = (placeName, generation) => {
    const service = new window.google.maps.places.PlacesService(placeInputRef.current);
    service.findPlaceFromQuery({ query: placeName, fields: ["geometry"] }, (results, status) => {
      if (generation !== initGenerationRef.current) return;

      if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();
        setLatLng({ lat, lng });
        setPlaceValue(placeName);
        placeInputRef.current.value = placeName;
      } else {
        // Log error for debugging
        qlog.debug(`Failed to geocode location "${placeName}": ${status}`);
        // Still set the input value so user can see what was attempted
        placeInputRef.current.value = placeName;
      }
    });
  };

  // Allow the around radius to be programatically set (used by initialRadius)
  const setAroundRadiusInMiles = (radiusInMiles) => {
    let radiusInMeters;
    const radius = parseInt(radiusInMiles, 10);

    switch (radius) {
      case 5:
        radiusInMeters = "8047";
        break;
      case 10:
        radiusInMeters = "16093";
        break;
      case 25:
        radiusInMeters = "40234";
        break;
      case 50:
        radiusInMeters = "80467";
        break;
      case 100:
        radiusInMeters = "160934";
        break;
      case 250:
        radiusInMeters = "402336";
        break;
      default:
        radiusInMeters = "all";
        break;
    }
    setAroundRadius(radiusInMeters);
  };

  const loader = new Loader({
    apiKey: globalThis.Qwoted.GOOGLE_CLIENTSIDE_API_KEY,
    version: "weekly",
  });

  // Set up Google autocomplete
  // Run-once on mount: initializes Google Maps Places API autocomplete.
  // Intentionally captures initial prop values (initialLocationName, initialLocationRadius) in closure.
  // Re-running this effect would create duplicate autocomplete instances and event listeners.
  useEffect(() => {
    // Captured before the await: a reset while importLibrary is pending bumps the generation.
    const initialGeneration = initGenerationRef.current;
    // Set only by this effect's cleanup (unmount), never by a reset/clear.
    let cancelled = false;

    const setupGooglePlacesAutocomplete = async () => {
      const { Autocomplete } = await loader.importLibrary("places");

      // Abort only for unmount or a torn-down input (Autocomplete against null throws), never for a
      // generation change: Autocomplete always installs while the component is alive.
      if (cancelled || !placeInputRef.current) return;

      const autocomplete = new Autocomplete(placeInputRef.current, { types: ["(cities)"] });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place || !place.geometry || !place.geometry.location) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const latLngResponse = {};

        // Guard on null, not truthiness: a valid coordinate can be 0 (the equator or the prime
        // meridian), and `if (lat && lng)` would silently drop such a place from the geo filter.
        if (lat != null && lng != null) {
          setPlaceValue(placeInputRef.current.value);
          latLngResponse.lat = lat;
          latLngResponse.lng = lng;
        }

        setLatLng(latLngResponse);
      });

      // Generation check suppresses a stale initial-location lookup: a reset that raced the import
      // must not resurrect the discarded value.
      if (initialLocationName && initGenerationRef.current === initialGeneration) {
        // Add a small delay to ensure the input element and Google Maps API are ready
        initTimeoutRef.current = setTimeout(() => {
          setPlace(initialLocationName, initialGeneration);

          // locationRadius doesn't make sense without locationName
          if (initialLocationRadius) {
            setAroundRadiusInMiles(initialLocationRadius);
          }
        }, 200);
      }
    };

    setupGooglePlacesAutocomplete();

    return () => {
      cancelled = true;
      clearTimeout(initTimeoutRef.current);
      initGenerationRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear the location when the X is clicked within the place box
  const processGeoSearchClear = () => {
    clearTimeout(initTimeoutRef.current);
    initGenerationRef.current += 1;
    placeInputRef.current.value = "";
    setPlaceValue(null);
    setLatLng({});
    setAroundRadiusInMiles("all");
    onActiveChange?.(false);
  };

  // Controlled reset: the parent bumps resetSignal to trigger the same clear the X button performs.
  // Without it the pill looks cleared while useConfigure above keeps aroundLatLng set, silently
  // excluding every record without _geoloc. Skipped on mount so an initial value doesn't fire a
  // spurious clear.
  const resetSignalRef = useRef(resetSignal);
  useEffect(() => {
    if (resetSignal === resetSignalRef.current) return;
    resetSignalRef.current = resetSignal;
    processGeoSearchClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  // Save the around radius when an distance is changed in select
  const handleAroundRadiusChange = (e) => {
    setAroundRadius(e.target.value);
  };

  return (
    <div className="ais-SearchBox">
      <form noValidate="" className="ais-SearchBox-form" action="" role="search" onSubmit={(e) => e.preventDefault()}>
        <input
          type="search"
          placeholder="Search near…"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck="false"
          className="ais-SearchBox-input autocomplete-place-input"
          ref={placeInputRef}
        />
        <button type="button" title="Submit your search query." className="ais-SearchBox-submit">
          <svg
            className="ais-SearchBox-submitIcon"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 14 20"
          >
            <path d={submitIconPath} />
          </svg>
        </button>
        {placeValue && (
          <button
            type="button"
            title="Clear the search query."
            className="ais-SearchBox-reset"
            onClick={processGeoSearchClear}
            hidden=""
          >
            <svg
              className="ais-SearchBox-resetIcon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              width="10"
              height="10"
            >
              <path d={resetIconPath} />
            </svg>
          </button>
        )}
      </form>
      <div className="input-group mt-3">
        <label className="input-group-text mb-0" htmlFor="near_within_distance">
          Within
        </label>
        <select
          name="near_within_distance"
          id="near_within_distance"
          value={aroundRadius}
          onChange={handleAroundRadiusChange}
          className="form-select"
        >
          {/* Values in meters */}
          <option value="all">{placeValue ? "Any distance" : ""}</option>
          <option value="8047">5 miles</option>
          <option value="16093">10 miles</option>
          <option value="40234">25 miles</option>
          <option value="80467">50 miles</option>
          <option value="160934">100 miles</option>
          <option value="402336">250 miles</option>
        </select>
      </div>
    </div>
  );
}

GeoSearchByPlace.propTypes = {
  initialLocationName: PropTypes.string,
  initialLocationRadius: PropTypes.string,
  onActiveChange: PropTypes.func,
  onLabelChange: PropTypes.func,
  resetSignal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default GeoSearchByPlace;
